import { useDispatch } from 'react-redux';
import { arrayMoveImmutable } from 'array-move';
import {
  setDraggedItemId,
  setDragOverIndex,
} from '../store/slices/editorUISlice';
import {
  reorderItems,
} from '../store/slices/timelineSlice';

const SECTION_WIDTH = 235;
const GAP = 50;

export function useDragAndDrop({
  timelineItems,
  isPreviewMode,
  isEditMode,
  fetchedMeeting,
  fetchedSections,
  user,
  sectionsListRef,
  isCreator,
  isContributor,
}) {
  const dispatch = useDispatch();

  const isItemAllowedToDrag = () => {
    if (isPreviewMode) return false;
    if (timelineItems.length <= 1) return false;
    
    // In create mode, all items are draggable
    if (!isEditMode) return true;
    
    // In edit mode, check permissions
    if (!fetchedMeeting) return false;
    
    // Contributors cannot drag/reorder sections (they can only add content)
    if (isContributor) {
      return false;
    }
    
    // Creator can drag all items
    if (isCreator) return true;
    
    // Default: allow drag for other elevated roles (e.g., org owner)
    return true;
  };

  // Handle drag start
  const handleDragStart = (e, itemIndex) => {
    const item = timelineItems[itemIndex];
    
    // Prevent drag if not allowed (e.g., in preview mode)
    if (!isItemAllowedToDrag(item)) {
      e.preventDefault();
      return;
    }
    
    e.dataTransfer.setData('itemIndex', itemIndex.toString());
    e.dataTransfer.effectAllowed = 'move';
    // Set dragged item ID for visual feedback
    dispatch(setDraggedItemId(timelineItems[itemIndex].id));
    
    // Create custom drag preview skeleton
    const dragPreview = document.createElement('div');
    dragPreview.style.width = '235px';
    dragPreview.style.height = '120px';
    dragPreview.style.borderRadius = '16px';
    dragPreview.style.backgroundColor = '#ffffff';
    dragPreview.style.border = '2px dashed #9CA3AF';
    dragPreview.style.display = 'flex';
    dragPreview.style.alignItems = 'center';
    dragPreview.style.justifyContent = 'center';
    dragPreview.style.boxSizing = 'border-box';
    dragPreview.style.pointerEvents = 'none';
    
    // Add a simple icon or text to indicate dragging
    const dragIcon = document.createElement('div');
    dragIcon.style.width = '40px';
    dragIcon.style.height = '40px';
    dragIcon.style.borderRadius = '8px';
    dragIcon.style.backgroundColor = '#F3F4F6';
    dragIcon.style.display = 'flex';
    dragIcon.style.alignItems = 'center';
    dragIcon.style.justifyContent = 'center';
    dragIcon.innerHTML = '⋮⋮';
    dragIcon.style.color = '#6B7280';
    dragIcon.style.fontSize = '20px';
    dragIcon.style.lineHeight = '1';
    dragIcon.style.fontWeight = 'bold';
    dragIcon.style.letterSpacing = '-2px';
    
    dragPreview.appendChild(dragIcon);
    
    // Position off-screen
    dragPreview.style.position = 'absolute';
    dragPreview.style.top = '-9999px';
    dragPreview.style.left = '-9999px';
    document.body.appendChild(dragPreview);
    
    // Set as drag image (offset to center)
    e.dataTransfer.setDragImage(dragPreview, 117.5, 60);
    
    // Clean up after drag starts
    setTimeout(() => {
      document.body.removeChild(dragPreview);
    }, 0);
  };

  // Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    // Calculate drop position for visual feedback based on gap position
    if (sectionsListRef.current) {
      // Find the inner div that has the translateX transform (first child div)
      const innerDiv = sectionsListRef.current.firstElementChild;
      if (!innerDiv || innerDiv.tagName !== 'DIV') return;
      
      const innerRect = innerDiv.getBoundingClientRect();
      const currentZoom = parseFloat(
        getComputedStyle(document.documentElement).getPropertyValue('--zoom').trim()
      ) || 1;
      // Calculate position relative to the inner div (which is translated)
      const xPosition = e.clientX - innerRect.left;
      const currentZoomedSectionWidth = SECTION_WIDTH * currentZoom;
      const currentZoomedGap = GAP * currentZoom;
      const slotWidth = currentZoomedSectionWidth + currentZoomedGap;
      
      // Calculate which slot we're in
      const slotIndex = Math.floor(xPosition / slotWidth);
      
      // Calculate position within the slot (0 to slotWidth)
      const positionInSlot = xPosition % slotWidth;
      
      // Determine if we're in the gap (after section, before next gap)
      // Gap is from SECTION_WIDTH to slotWidth
      let gapIndex;
      if (positionInSlot < currentZoomedSectionWidth) {
        // We're over a section - determine which gap based on which half
        if (positionInSlot < currentZoomedSectionWidth / 2) {
          // Left half of section - gap before this section
          gapIndex = slotIndex;
        } else {
          // Right half of section - gap after this section
          gapIndex = slotIndex + 1;
        }
      } else {
        // We're directly in the gap
        gapIndex = slotIndex + 1;
      }
      
      // Gap N means insert at position N
      // Clamp to valid range: 0 to timelineItems.length
      const dropIndex = Math.max(0, Math.min(gapIndex, timelineItems.length));
      dispatch(setDragOverIndex(dropIndex));
    }
  };

  // Handle drop
  const handleSectionDrop = (e) => {
    e.preventDefault();
    const draggedIndexStr = e.dataTransfer.getData('itemIndex');
    if (!draggedIndexStr) return;
    
    const draggedIndex = parseInt(draggedIndexStr, 10);
    if (Number.isNaN(draggedIndex)) return;
    
    // Calculate drop position based on gap position
    if (!sectionsListRef.current) return;
    
    // Find the inner div that has the translateX transform (first child div)
    const innerDiv = sectionsListRef.current.firstElementChild;
    if (!innerDiv || innerDiv.tagName !== 'DIV') return;
    
    const innerRect = innerDiv.getBoundingClientRect();
    const currentZoom = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--zoom').trim()
    ) || 1;
    // Calculate position relative to the inner div (which is translated)
    const xPosition = e.clientX - innerRect.left;
    const currentZoomedSectionWidth = SECTION_WIDTH * currentZoom;
    const currentZoomedGap = GAP * currentZoom;
    const slotWidth = currentZoomedSectionWidth + currentZoomedGap;
    
    // Calculate which slot we're in
    const slotIndex = Math.floor(xPosition / slotWidth);
    
    // Calculate position within the slot (0 to slotWidth)
    const positionInSlot = xPosition % slotWidth;
    
    // Determine if we're in the gap (after section, before next gap)
    let gapIndex;
    if (positionInSlot < currentZoomedSectionWidth) {
      // We're over a section - determine which gap based on which half
      if (positionInSlot < currentZoomedSectionWidth / 2) {
        // Left half of section - gap before this section
        gapIndex = slotIndex;
      } else {
        // Right half of section - gap after this section
        gapIndex = slotIndex + 1;
      }
    } else {
      // We're directly in the gap
      gapIndex = slotIndex + 1;
    }
    
    // Gap N means insert at position N
    // Clamp to valid range: 0 to timelineItems.length
    let newIndex = Math.max(0, Math.min(gapIndex, timelineItems.length));
    
    // If the gap is at the same position as the dragged item, skip it
    // (can't drop at the same position)
    if (newIndex === draggedIndex) {
      // If dragging right, move to next gap
      // If dragging left, move to previous gap
      // For now, just skip - don't move
      dispatch(setDraggedItemId(null));
      dispatch(setDragOverIndex(null));
      return;
    }
    
    // Adjust index when dragging: if newIndex > draggedIndex, we need to account for
    // the fact that the dragged item will be removed, shifting indices
    if (newIndex > draggedIndex) {
      // When moving right, the target position shifts by -1 because dragged item is removed
      newIndex -= 1;
    }
    
    // Apply position constraints for non-creators
    if (isEditMode && fetchedMeeting && !isCreator && user) {
      const draggedItem = timelineItems[draggedIndex];
      const draggedSectionId = draggedItem.metadata?.sectionId;
      
      if (draggedSectionId) {
        const draggedSection = fetchedSections?.find((s) => s.id === draggedSectionId);
        
        // If dragging own item, can't move before other users' items
        if (draggedSection && draggedSection.userId === user.id) {
          // Count how many items belong to other users before the drop position
          const otherUsersItemsBeforeDrop = timelineItems
            .slice(0, newIndex)
            .filter((item) => {
              const sectionId = item.metadata?.sectionId;
              if (!sectionId) return false;
              const section = fetchedSections?.find((s) => s.id === sectionId);
              return section && section.userId !== user.id;
            }).length;
          
          // Can't move before other users' items
          if (otherUsersItemsBeforeDrop > 0) {
            // Adjust to after the last other user's item
            let lastOtherUserIndex = -1;
            for (let idx = newIndex - 1; idx >= 0; idx -= 1) {
              const item = timelineItems[idx];
              const sectionId = item.metadata?.sectionId;
              if (sectionId) {
                const section = fetchedSections?.find((s) => s.id === sectionId);
                if (section && section.userId !== user.id) {
                  lastOtherUserIndex = idx;
                  break;
                }
              }
            }
            
            if (lastOtherUserIndex >= 0) {
              newIndex = lastOtherUserIndex + 1;
            }
          }
        }
      }
    }
    
    // Reorder items using arrayMoveImmutable
    const newItems = arrayMoveImmutable(timelineItems, draggedIndex, newIndex);
    
    // Dispatch to Redux - it will automatically recalculate order and start_time
    dispatch({ ...reorderItems(newItems), meta: { undoable: true } });
    
    // Clear drag state
    dispatch(setDraggedItemId(null));
    dispatch(setDragOverIndex(null));
  };

  // Handle drag end (cleanup)
  const handleDragEnd = () => {
    dispatch(setDraggedItemId(null));
    dispatch(setDragOverIndex(null));
  };

  return {
    isItemAllowedToDrag,
    handleDragStart,
    handleDragOver,
    handleSectionDrop,
    handleDragEnd,
  };
}

